// ============================================================================
// DATA TRANSFORMATION LOGIC FOR PLACE ORDER MODAL
// ============================================================================

import { ProductData, OrderDetailData, OrderPayload } from "./types";

// ============================================================================
// PRODUCT TRANSFORMATION FUNCTIONS
// ============================================================================


// ============================================================================
// MAIN TRANSFORMATION FUNCTION
// ============================================================================

export const transformToApiPayload = (products: ProductData[], orderDetails: OrderDetailData): OrderPayload => {
  console.log("=== TRANSFORMING PRODUCT TO API FORMAT ===");
  console.log("Input products:", products);
  console.log("Order details:", orderDetails);
  
  const transformedProducts = products.map((product) => {
    console.log("=== TRANSFORMING PRODUCT TO API FORMAT ===");
    console.log("Input product:", product);
    
    const transformedProduct: any = {
      id: product.id || `Product-${Math.random().toString(36).substr(2, 9)}`,
      name: product.label || product.id || `Product-${Math.random().toString(36).substr(2, 9)}`,
      type: product.type || 'door',
      label: product.label || '',
      location: product.location || '',
      notes: product.notes || '',
      evaOrder: product.evaOrder || '',
      color: product.color || '',
      netCount: product.netCount || 0
    };


    // Handle direct arrays (no itemTypes wrapper)
    const itemTypes = ['frame', 'panel', 'panelWindow', 'net', 'openableType'];
    
    itemTypes.forEach((itemType) => {
      const items = product[itemType as keyof ProductData] as any[];
      if (Array.isArray(items) && items.length > 0) {
        console.log(`Processing ${itemType} items:`, items);
        transformedProduct[itemType] = items.map((item: any) => ({
          id: item.id || `${itemType}-${Math.random().toString(36).substr(2, 9)}`,
          type: item.type || itemType,
          quantity: item.quantity || 1,
          width: item.width || '',
          height: item.height || '',
          number: item.number || '',
          material: item.material || '',
          color: item.color || '',
          finish: item.finish || '',
          member_required: item.member_required ?? false,
          bar_required: item.bar_required ?? false,
          glass_required: item.glass_required ?? false,
          assigned_to: item.assigned_to || '',
          estimated_hours: item.estimated_hours || 0
        }));
      } else {
        transformedProduct[itemType] = [];
      }
    });

    console.log("Transformed product:", transformedProduct);
    return transformedProduct;
  });

  return {
    customerId: orderDetails?.customerId,
    siteId: orderDetails?.siteId || '',
    orderLabel: orderDetails?.orderLabel || '',
    orderNotes: orderDetails?.orderNotes || '',
    totalAmount: orderDetails?.totalAmount || 0,
    priority: orderDetails?.priority,
    paymentStatus: orderDetails?.paymentStatus,
    dueDate: orderDetails?.dueDate,
    notes: orderDetails?.notes,
    products: transformedProducts
  };
};

// ============================================================================
// REDUX ORDER DATA TRANSFORMATION
// ============================================================================

export const transformToReduxOrderData = (apiPayload: OrderPayload) => {
  return {
    customerId: apiPayload.customerId,
    siteId: apiPayload.siteId,
    orderLabel: apiPayload.orderLabel,
    orderNotes: apiPayload.orderNotes,
    totalAmount: apiPayload.totalAmount,
    priority: apiPayload.priority,
    paymentStatus: apiPayload.paymentStatus,
    dueDate: apiPayload.dueDate,
    notes: apiPayload.notes,
    products: apiPayload.products.map((product, index) => ({
      id: product.id || `product-${index}`,
      type: product.type as any,
      label: product.label || '',
      location: product.location || '',
      notes: product.notes || '',
      evaOrder: product.evaOrder || '',
      netCount: product.netCount || 0,
      // Direct arrays instead of itemTypes wrapper
      frame: (product.frame || []).map((item, itemIndex) => ({
        id: item.id || `frame-${index}-${itemIndex}`,
        type: item.type || "frame",
        number: item.number || '',
        width: item.width || "",
        height: item.height || "",
        quantity: item.quantity || 1,
        material: item.material || '',
        color: item.color || '',
        finish: item.finish || '',
        member_required: item.member_required ?? false,
        bar_required: item.bar_required ?? false,
        glass_required: item.glass_required ?? false,
        assigned_to: item.assigned_to || "",
        estimated_hours: item.estimated_hours || 0
      })),
      panel: (product.panel || []).map((item, itemIndex) => ({
        id: item.id || `panel-${index}-${itemIndex}`,
        type: item.type || "panel",
        number: item.number || '',
        width: item.width || "",
        height: item.height || "",
        quantity: item.quantity || 1,
        material: item.material || '',
        color: item.color || '',
        finish: item.finish || '',
        member_required: item.member_required ?? false,
        bar_required: item.bar_required ?? false,
        glass_required: item.glass_required ?? false,
        assigned_to: item.assigned_to || "",
        estimated_hours: item.estimated_hours || 0
      })),
      panelWindow: (product.panelWindow || []).map((item, itemIndex) => ({
        id: item.id || `panelWindow-${index}-${itemIndex}`,
        type: item.type || "panelWindow",
        number: item.number || '',
        width: item.width || "",
        height: item.height || "",
        quantity: item.quantity || 1,
        material: item.material || '',
        color: item.color || '',
        finish: item.finish || '',
        member_required: item.member_required ?? false,
        bar_required: item.bar_required ?? false,
        glass_required: item.glass_required ?? false,
        assigned_to: item.assigned_to || "",
        estimated_hours: item.estimated_hours || 0
      })),
      net: (product.net || []).map((item, itemIndex) => ({
        id: item.id || `net-${index}-${itemIndex}`,
        type: item.type || "net",
        number: item.number || '',
        width: item.width || "",
        height: item.height || "",
        quantity: item.quantity || 1,
        material: item.material || '',
        color: item.color || '',
        finish: item.finish || '',
        member_required: item.member_required ?? false,
        bar_required: item.bar_required ?? false,
        glass_required: item.glass_required ?? false,
        assigned_to: item.assigned_to || "",
        estimated_hours: item.estimated_hours || 0
      })),
      openableType: (product.openableType || []).map((item, itemIndex) => ({
        id: item.id || `openable-${index}-${itemIndex}`,
        type: item.type || "openableType",
        number: item.number || '',
        width: item.width || "",
        height: item.height || "",
        quantity: item.quantity || 1,
        material: item.material || '',
        color: item.color || '',
        finish: item.finish || '',
        member_required: item.member_required ?? false,
        bar_required: item.bar_required ?? false,
        glass_required: item.glass_required ?? false,
        assigned_to: item.assigned_to || "",
        estimated_hours: item.estimated_hours || 0
      }))
    }))
  };
};
